'use client';

import * as React from 'react';
import { ChevronsUpDown, RefreshCwIcon } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { useOptionalFilterState, useSetUrlById } from '@/hooks/useFilter';
import {
  useBusinessUnitOptions,
  useProjectOptions,
  useProjectsLoading,
} from '@/hooks/useProjectOptions';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';
import { CompanyLogo } from './CompanyLogo';
import {
  ProjectContextIcon,
  ProjectContextRow,
} from './Inputs/ProjectContextOption';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';

export function ProjectSwitcher() {
  const filter = useOptionalFilterState();
  const loading = useProjectsLoading();
  const projects = useProjectOptions();
  const businessUnits = useBusinessUnitOptions().toSorted((a, b) =>
    a.name.localeCompare(b.name)
  );
  const { isMobile } = useSidebar();
  const setUrlById = useSetUrlById();
  const [open, setOpen] = React.useState(false);

  // CommandItem (cmdk) doesn't close the dropdown on select the way a
  // DropdownMenuItem would, so we close it ourselves.
  const selectAndClose = (value: Parameters<typeof setUrlById>[0]) => {
    setUrlById(value);
    setOpen(false);
  };

  // the "Todos los Proyectos" company-level option (no project, no BU)
  const allProjectsOption = projects.find(
    (project) => project.value.project_id === undefined
  );
  // one group per business unit: the BU itself, then its projects
  const businessUnitGroups = businessUnits.map((businessUnit) => ({
    businessUnit,
    projects: projects
      .filter((project) => project.value.business_unit_id === businessUnit.id)
      .toSorted((a, b) => a.label.localeCompare(b.label)),
  }));
  const isEmpty = !allProjectsOption && !businessUnitGroups.length;

  // Make the current context level explicit in the trigger. A business unit and
  // a project can share a name, so the level word disambiguates them.
  // 'Todos los proyectos' is a claim about the user's reach, so it is only for
  // a context they actually selected: with none selected yet the trigger asks
  // for one instead of asserting company-wide access (BIL-4887).
  const contextLabel =
    filter.kind !== 'selected'
      ? filter.label
      : filter.names.project
        ? `Proyecto · ${filter.names.project}`
        : filter.names.business_unit
          ? `Unidad de negocio · ${filter.names.business_unit}`
          : 'Todos los proyectos';

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="flex aspect-square border size-8 items-center justify-center rounded-lg bg-sidebar-muted text-sidebar-primary-foreground p-0">
                <CompanyLogo className={'size-8'} />
              </div>
              <div className="grid flex-1 gap-1 text-left text-sm leading-tight">
                {loading ? (
                  // until the roles are in we know neither the company nor the
                  // context, and naming one we can't back up misleads the user
                  <>
                    <Skeleton className="h-3.5 w-24" />
                    <Skeleton className="h-3 w-32" />
                  </>
                ) : (
                  <>
                    <span className="truncate font-semibold">
                      {filter.names.company}
                    </span>
                    <span className="truncate text-xs">{contextLabel}</span>
                  </>
                )}
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            align="start"
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <Link to={'/choose_company'}>
              <DropdownMenuItem className="gap-2 p-2">
                <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                  <RefreshCwIcon className="size-4" />
                </div>
                <div className="font-medium text-muted-foreground">
                  Cambiar Compañía
                </div>
              </DropdownMenuItem>
            </Link>
            <DropdownMenuSeparator />
            <Command
              // BIL-3335: stop propagation to prevent the dropdown items from interpreting input as keyboard shortcuts
              onKeyDown={(e) => e.stopPropagation()}
            >
              <CommandInput placeholder="Buscar proyecto..." autoFocus />
              <CommandList>
                {loading ? (
                  <CommandGroup heading="Proyectos">
                    <CommandItem className="gap-2 p-2" disabled>
                      <ProjectContextIcon level="project" />
                      Cargando proyectos...
                    </CommandItem>
                  </CommandGroup>
                ) : isEmpty ? (
                  <CommandGroup heading="Proyectos">
                    <CommandItem className="gap-2 p-2" disabled>
                      <ProjectContextIcon level="project" />
                      No tenés acceso a ningún proyecto
                    </CommandItem>
                  </CommandGroup>
                ) : (
                  <>
                    {allProjectsOption ? (
                      <CommandGroup>
                        <CommandItem
                          key={allProjectsOption.label}
                          value="__all_projects__"
                          keywords={[allProjectsOption.label]}
                          onSelect={() =>
                            selectAndClose(allProjectsOption.value)
                          }
                          className="p-2"
                        >
                          <ProjectContextRow
                            level="company"
                            label={allProjectsOption.label}
                          />
                        </CommandItem>
                      </CommandGroup>
                    ) : null}
                    {businessUnitGroups.map(({ businessUnit, projects }) => (
                      <CommandGroup
                        key={businessUnit.id}
                        heading={businessUnit.name}
                      >
                        {/* the business unit is only a destination when the
                            user's roles reach it; otherwise the group heading
                            above is all it gets (BIL-4887) */}
                        {businessUnit.selectable ? (
                          <CommandItem
                            key={`business-unit-${businessUnit.id}`}
                            value={`business-unit-${businessUnit.id}`}
                            keywords={[businessUnit.name]}
                            onSelect={() =>
                              selectAndClose({
                                company_id: businessUnit.company_id,
                                business_unit_id: businessUnit.id,
                              })
                            }
                            className="p-2"
                          >
                            <ProjectContextRow
                              level="business-unit"
                              label={businessUnit.name}
                            />
                          </CommandItem>
                        ) : null}
                        {projects.map((project) => (
                          <CommandItem
                            key={project.label}
                            value={`project-${project.value.project_id}`}
                            keywords={[project.label]}
                            onSelect={() => selectAndClose(project.value)}
                            className="p-2"
                          >
                            <ProjectContextRow
                              level="project"
                              label={project.label}
                              indented
                            />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    ))}
                  </>
                )}
              </CommandList>
            </Command>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
